import { Typography, theme } from 'antd'
import { env, updateShopId, useTranslate, useWindowDimensions } from 'utils'
import { Loading, Logo, PageContentWrapper, PageError, VerticalContainer } from 'components/styled'
import { FormSection } from 'components/form-section/form-section'
import { Avatar } from 'components/avatar/avatar'
import { VerticalPaginatedList } from 'components/vertical-paginated-list/vertical-paginated-list'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useInstalledApp, useShops } from 'api'
import { useEffect, useState } from 'react'
import { Shop } from 'types'
const { Paragraph } = Typography

// Reached from /oauth/authorization without a shopId (e.g. an install started from a
// third-party marketplace): the merchant picks a shop, then the OAuth flow continues with it.
export const AppAuthorizePage = () => {

    //hooks:
    const navigate = useNavigate()
    const [ searchParams ] = useSearchParams()
    const id          = searchParams.get('app_id')
    const clientId    = searchParams.get('client_id')
    const redirectURI = searchParams.get('redirect_uri')
    const scopes      = searchParams.getAll('scopes[]')

    //if no payload:
    useEffect(() => {
        (!id || !clientId || !redirectURI || !scopes.length) && navigate('/404')
    }, [id, clientId, redirectURI, scopes.length])

    return (id && clientId && redirectURI && scopes.length) ? (
        <AppAuthorize
            id={id}
            clientId={clientId}
            redirectURI={redirectURI}
            scopes={scopes}
        />
    ) : <></>

}

type Props = {
    id: string
    clientId: string
    redirectURI: string
    scopes: string[]
}

const AppAuthorize = ({ id, clientId, redirectURI, scopes }: Props) => {

    //hooks:
    const { __ } = useTranslate()
    const { SM, MD } = useWindowDimensions()
    const navigate = useNavigate()
    const { token: {
        paddingXXS,
        paddingXS,
        padding,
        paddingMD,
        paddingLG,
        marginSM,
        marginXL,
        fontSizeLG,
        borderRadius
    }} = theme.useToken()

    //state:
    const [ search, setSearch ] = useState<string | null>(null)
    const [ isRedirecting, setIsRedirecting ] = useState(false)

    //api:
    const { data: app, isLoading: isAppLoading } = useInstalledApp(id, () => navigate('/404'))
    const {
        data: shops,
        total,
        pageNum,
        totalPages,
        isLoading: isShopsLoading,
        isPreviousData,
        isNextDisabled,
        isPreviousDisabled,
        fetchNext,
        fetchPrevious
    } = useShops(search, 10)

    const authorize = (shopId: string) => {
        setIsRedirecting(true)
        updateShopId(shopId) // the consent page reads the selected shop
        const params = new URLSearchParams({ clientId, redirectUri: redirectURI, shopId })
        scopes.forEach(scope => params.append('scopes[]', scope))
        window.location.replace(`${env().REST_API_BASE_URL}/oauth/authorization?${params}`)
    }

    //a single shop leaves nothing to choose:
    const onlyShop = (!search && total === 1 && shops) ? shops[0] : null
    useEffect(() => {
        onlyShop && authorize(onlyShop.id)
    }, [onlyShop?.id])

    return (isAppLoading || isRedirecting || onlyShop) ? <Loading/> : !app ? <PageError/> : (
        <PageContentWrapper flex>

            <VerticalContainer style={{
                flex: 1,
                padding: SM ? `${padding}px` : 0,
                alignItems: 'center',
                justifyContent: 'center',
                gap: marginXL
            }}>

                {/* logo */}
                <Logo withName={true}/>

                <FormSection
                    title={__`shops`}
                    searchInput={{
                        placeholder: __`search_shops`,
                        onSearch: setSearch
                    }}
                    style={{
                        width: '100%',
                        maxWidth: 600,
                        height: 'fit-content'
                    }}
                    childrenWrapperStyle={{ padding: SM ? `0 ${paddingMD}px` : 0 }}
                >

                    <VerticalContainer style={{ gap: marginSM }}>

                        <Paragraph style={{ margin: 0, fontSize: fontSizeLG }}>
                            {__(`choose_the_shop_to_connect_app_name_to`, { 'appName': app.name })}
                        </Paragraph>

                        <VerticalPaginatedList
                            isFirstLoading={isShopsLoading}
                            isForbidden={shops === null}
                            isError={shops === false}
                            notFound={(shops && shops.length === 0) ?? false}
                            totalPages={totalPages}
                            pageNum={pageNum}
                            isPreviousData={isPreviousData}
                            isNextDisabled={isNextDisabled}
                            isPreviousDisabled={isPreviousDisabled}
                            fetchNext={fetchNext}
                            fetchPrevious={fetchPrevious}
                        >
                            {shops && shops.map((shop: Shop) =>
                                <Avatar
                                    key={shop.id}
                                    size='tiny'
                                    name={shop.name}
                                    image={shop.image.path}
                                    style={{
                                        width: '100%',
                                        padding: MD ? `${paddingXXS}px ${paddingXS}px` : `${paddingXXS}px ${paddingLG}px`,
                                        borderRadius: MD ? borderRadius : 0
                                    }}
                                    onClick={() => authorize(shop.id)}
                                />
                            )}
                        </VerticalPaginatedList>

                    </VerticalContainer>

                </FormSection>

            </VerticalContainer>

        </PageContentWrapper>
    )

}
